import { Component } from '@theme/component';
import { computeSchedule } from '@theme/civital-dispatch-schedule';

/**
 * Counts down to the same-day dispatch cut-off and names the resulting delivery date.
 *
 * The scheduling rule lives in @theme/civital-dispatch-schedule so it can be tested
 * without a DOM. This component only renders it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [hours] - Whole hours remaining.
 * @property {HTMLElement} [minutes] - Whole minutes remaining.
 * @property {HTMLElement} [seconds] - Whole seconds remaining.
 * @property {HTMLElement} [dispatchDate] - Localised date the order leaves the warehouse.
 * @property {HTMLElement} [deliveryDate] - Localised date the order arrives.
 * @property {HTMLElement} [active] - Shown while today's cut-off is still open.
 * @property {HTMLElement} [expired] - Shown once today's cut-off has passed.
 *
 * @extends {Component<Refs>}
 */
class CivitalDispatchCountdown extends Component {
  /** @type {number | undefined} */
  #timer;

  /** @type {Intl.DateTimeFormat | undefined} */
  #dateFormat;

  connectedCallback() {
    super.connectedCallback();

    this.#render();
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // The id can legitimately be 0, so test for undefined rather than truthiness.
    if (this.#timer !== undefined) window.clearTimeout(this.#timer);
  }

  /**
   * Ticks on a timeout aligned to the next boundary rather than a fixed interval.
   *
   * setInterval drifts against the wall clock. The value shown stays correct either way
   * because every render reads Date.now(), but the moments it updates slide, and a
   * seconds digit that renders at .999 then .002 visibly skips or repeats a number.
   * Waiting out only the remainder of the current period re-aligns on every tick.
   *
   * Instances that do not show seconds — the product page's delivery promise — have
   * nothing that changes more than once a minute, so they tick at that rate instead.
   */
  #schedule = () => {
    const period = this.refs.seconds ? 1000 : 60_000;

    this.#timer = window.setTimeout(() => {
      this.#render();
      this.#schedule();
    }, period - (Date.now() % period));
  };

  /** @returns {number} Cut-off hour in London time, 0-23. */
  get cutoffHour() {
    return Number(this.dataset.cutoffHour ?? 16);
  }

  /**
   * Non-working days as YYYY-MM-DD strings, seeded from a theme setting rather than
   * fetched at runtime — a header that cannot render until a third-party request
   * resolves is a liability, and the UK publishes these years ahead.
   *
   * @returns {Set<string>}
   */
  get bankHolidays() {
    try {
      const parsed = JSON.parse(this.dataset.bankHolidays || '[]');

      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (_) {
      return new Set();
    }
  }

  /**
   * @param {{year: number, month: number, day: number}} date
   * @returns {string}
   */
  #formatDate(date) {
    const utc = new Date(Date.UTC(date.year, date.month - 1, date.day));

    // Built once and kept. Constructing a formatter is the expensive part of Intl, and
    // this ran twice per tick to produce two strings that change once a day.
    this.#dateFormat ??= new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'short',
    });

    return this.#dateFormat.format(utc);
  }

  /**
   * Write text into a ref that may be a single element or, when the same ref name is
   * used in more than one branch, an array of them. Horizon's Component keeps only the
   * last element for a plain duplicate name, so repeated refs are declared as `name[]`.
   *
   * @param {Element | Element[] | undefined} ref
   * @param {string} value
   */
  #setText(ref, value) {
    if (!ref) return;

    for (const element of Array.isArray(ref) ? ref : [ref]) {
      // Assigning an unchanged string still counts as a mutation. Four of these fired
      // every second where at most one digit had moved, which was enough churn to keep
      // re-rendering the element panel in devtools.
      if (element.textContent !== value) element.textContent = value;
    }
  }

  #render = () => {
    const { open, cutoff, dispatch, delivery } = computeSchedule({
      now: new Date(),
      cutoffHour: this.cutoffHour,
      bankHolidays: this.bankHolidays,
    });

    const totalSeconds = Math.max(0, Math.floor((cutoff.getTime() - Date.now()) / 1000));

    const { hours, minutes, seconds, dispatchDate, deliveryDate, active, expired } = this.refs;

    this.#setText(hours, String(Math.floor(totalSeconds / 3600)));
    this.#setText(minutes, String(Math.floor((totalSeconds % 3600) / 60)));
    this.#setText(seconds, String(totalSeconds % 60));

    // Dispatch and delivery are different days. Labelling one with the other's date is
    // the kind of mistake a customer only notices when their order is late.
    this.#setText(dispatchDate, this.#formatDate(dispatch));
    this.#setText(deliveryDate, this.#formatDate(delivery));

    this.classList.toggle('civital-dispatch--closed', !open);

    for (const element of Array.isArray(active) ? active : [active]) {
      if (element) /** @type {HTMLElement} */ (element).hidden = !open;
    }

    for (const element of Array.isArray(expired) ? expired : [expired]) {
      if (element) /** @type {HTMLElement} */ (element).hidden = open;
    }
  };
}

if (!customElements.get('civital-dispatch-countdown')) {
  customElements.define('civital-dispatch-countdown', CivitalDispatchCountdown);
}
